import styled, { css } from 'styled-components';
import { Icon } from '../../../../styled-components/components/Icon';
import { size } from '../../../../styled-components/layout/helpers';
import { noTransform } from '../../../../styled-components/layout/keyframes';
import { ButtonReset } from '../../../../styled-components/layout/reset';
import { externalLinkIconAfter } from '../../../../styled-components/layout/snippets';

export const Wrapper = styled.div`
  --_mainnav-p-block: ${size(6)};
  --_mainnav-p-inline: ${size(1)};

  position: relative;
  width: var(--navigation-mainnav-width);
  border-left: 1px solid var(--navigation-border-color);
  background-color: var(--navigation-bg);

  ${(props) => props.theme.breakpoint.Lg} {
    border-top: 1px solid var(--navigation-border-color);
    border-top-left-radius: 1.5rem;

    // Extension of border-top to go below the main content border-radius
    &::after {
      content: "";
      position: absolute;
      top: -1px;
      left: 100%;
      width: 1.5rem;
      height: 1px;
      background-color: var(--navigation-border-color);
    }
  }

  ${(props) => props.theme.breakpoint.MdLtLg} {
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(100% - var(--navigation-effect-width));
    border-top: 1px solid var(--navigation-border-color);
    z-index: ${(props) => props.theme.zindex.mainNavigationFoldout};
  }
`;

export const CloseBtn = styled(ButtonReset)`
  --_btn-size: ${size(4)};

  position: absolute;
  top: ${size(3)};
  left: 100%;
  margin-left: calc(var(--_btn-size) * -0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--_btn-size);
  height: var(--_btn-size);
  background-color: var(--c-bg-main);
  color: var(--c-text-muted);
  border: 1px solid var(--navigation-border-color);
  border-radius: 50%;
  font-size: ${size(1.25)};
  transform-origin: 50%;
  transition: transform 200ms ease;
  z-index: var(--z-index-mainnav-close-btn);

  &:active {
    transform: scale(0.85);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.1),
      0 4px 4px rgba(0, 0, 0, 0.09),
      0 8px 5px rgba(0, 0, 0, 0.05),
      0 15px 6px rgba(0, 0, 0, 0.02);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  ${(props) => props.theme.breakpoint.LtMd} {
    display: none;
  }
`;

export const Overflow = styled.div`
  width: 100%;
  height: 100%;
  padding: var(--_mainnav-p-block) var(--_mainnav-p-inline);
  overflow: auto;
  overscroll-behavior: contain;
`;

export const List = styled.div`
  display: grid;
  gap: ${size(0.25)};

  & & {
    padding-left: ${size(1.5)};
  }
`;

interface ItemProps {
  $isActive?: boolean;
  $childIsActive?: boolean;
}

export const Item = styled.div<ItemProps>`
  --_item-p-block: ${size(1.75)};
  --_item-p-inline: ${size(2)};
  --_item-gap: ${size(0.5)};

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--_item-gap);
  width: 100%;
  padding: var(--_item-p-block) var(--_item-p-inline);
  background-color: var(--_item-bg, transparent);
  border-radius: 100vmax;
  color: var(--navigation-fg); // Reset color if used as a link
  font-size: ${(props) => props.theme.font.size.small};
  font-weight: var(--font-weight-regular);
  line-height: ${(props) => props.theme.font.size.xlarge};
  text-decoration: none;
  overflow: hidden;
  text-align: left;

  ${({ $isActive, $childIsActive }) =>
    $isActive
      ? css`
          --_item-bg: var(--navigation-active-bg);
        `
      : $childIsActive
        ? css`
            --_item-bg: var(--navigation-hover-bg);
          `
        : css`
            &:hover {
              --_item-bg: var(--navigation-hover-bg);
            }
          `}

  ${Icon} {
    flex-shrink: 0;
    font-size: ${(props) => props.theme.font.size.regular};
  }

  // Add external link icon to external links
  &[target='_blank'] {
    ${externalLinkIconAfter};
  }
`;

export const ItemLabel = styled.span`
  flex: 1 1 auto;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Notification = styled.div`
  --_notification-p-block: ${size(0.5)};
  --_notification-p-inline: ${size(1)};

  flex-shrink: 0;
  min-width: ${size(3)};
  padding: var(--_notification-p-block) var(--_notification-p-inline);
  border-radius: ${size(1)};
  background-color: var(--navigation-accent-bg);
  color: var(--navigation-accent-fg);
  font-size: ${(props) => props.theme.font.size.xsmall};
  line-height: ${(props) => props.theme.font.size.regular};
  font-weight: var(--font-weight-bold);
  text-align: center;

  // Animation
  transform: scale(0);
  transform-origin: 50%;
  animation: ${noTransform} 200ms 200ms cubic-bezier(0.47, 1.64, 0.41, 0.8) forwards;
`;
